import BigNumber from 'bignumber.js'
import FormValidator from 'config/constants/formValidator'
import { ChainIdEnum } from 'config/constants/network'
import { BIG_ZERO } from 'config/constants/number'
import { RequiredProvidePhoneNumberBankCodes } from 'config/constants/payment'
import { HUSD_TOKEN } from 'config/constants/tokens'
import { PaymentMethod, Token, TokenAmount } from 'config/types'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import { DepositRolloverInfo, WithdrawFiatPaymentMethod } from 'config/types/payment'
import useForm from 'hooks/useForm'

import { FiatPaymentTransaction } from 'config/types/transaction'
import { ValidationError } from 'config/types/validator'

import useModal from 'hooks/useModal'
import { useRequest } from 'hooks/useRequest'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PaymentService from 'services/PaymentService'
import { useAppDispatch, useAppSelector } from 'state'
import { useTokenInfo, useTokenPaymentMethod } from 'state/app/hooks'
import { useAuthBy, useUserInfo } from 'state/auth/hooks'
import { useTokenAppBalance } from 'state/profile/hooks'
import { getChainCode } from 'utils'

import { addPendingWithdrawTransaction } from 'state/transaction/action'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { isTonChain, isXrpChain } from 'utils/network'
import { checkTokenEqual, getNetworkCodeType, getNetworkType, validateUserAddressByNetwokType } from 'utils/token'
import useDepositRolloverModal from 'views/Payment/components/DepositRollover/DepositRolloverModal/useDepositRolloverModal'
import { useWithdrawInfo } from 'views/Payment/components/Withdraw/WithdrawByFiat/hooks'
import WithdrawInProgressModal from 'views/Payment/components/Withdraw/WithdrawByFiat/WithdrawInProgressModal'
import {
  useDepositRolloverOverview,
  useWithdrawAddressNotAllowed,
} from 'views/Payment/components/Withdraw/WithdrawCryptos/hooks'

export enum WithdrawModeEnums {
  CRYPTO = 'crypto',
  FIAT = 'fiat',
}

type IUseWithdrawBaseControlsProps = {
  mode: WithdrawModeEnums
  selectedToken: Token
}

type IUseWithdrawCryptoControlsProps = IUseWithdrawBaseControlsProps & {
  mode: WithdrawModeEnums.CRYPTO
  bonuses: WageringConditionBonus[]
}

type IUseWithdrawFiatControlsProps = IUseWithdrawBaseControlsProps & {
  mode: WithdrawModeEnums.FIAT
}

type UseWithdrawControlProps = IUseWithdrawCryptoControlsProps | IUseWithdrawFiatControlsProps

export type WithdrawCryptoControls = ReturnType<typeof useWithdrawCrypto>
export type WithdrawFiatControls = ReturnType<typeof useWithdrawFiat>

export type WithdrawCryptoControl = {
  mode: WithdrawModeEnums.CRYPTO
  controls: WithdrawCryptoControls
}
export type WithdrawFiatControl = {
  mode: WithdrawModeEnums.FIAT
  controls: WithdrawFiatControls
}

const useWithdrawRestrictAmounts = (selectedToken) => {
  const { execute } = useRequest()
  const [withdrawFeeAmount, setWithdrawFeeAmount] = useState<string>(null)
  const [withdrawLockAmount, setWithdrawLockAmount] = useState<{
    lockAmount: BigNumber
    bonusId?: number
  }>({ lockAmount: BIG_ZERO })
  const [depositRolloverList, setDepositRolloverList] = useState<DepositRolloverInfo[]>([])

  useEffect(() => {
    if (selectedToken === HUSD_TOKEN) return

    const fetchFee = async () => {
      const response = await execute(
        PaymentService.getWithdrawInfo({
          chainType: getNetworkCodeType(selectedToken.network),
          chainCode: getChainCode(ChainIdEnum[selectedToken.network]),
          currency: selectedToken.code,
        }),
      )

      if (response?.code === 'success') {
        setWithdrawFeeAmount(response.data.withdrawFee)
        setWithdrawLockAmount({
          lockAmount: new BigNumber(response.data.lockBonusAmount || 0),
          bonusId: response.data.voucherId || null,
        })
        setDepositRolloverList(response.data.depositRolloverList)
        return
      }

      if (depositRolloverList.length > 0) setDepositRolloverList([])
      if (withdrawLockAmount.lockAmount.isGreaterThan(BIG_ZERO)) setWithdrawLockAmount({ lockAmount: BIG_ZERO })
    }

    fetchFee()
  }, [selectedToken])

  return useMemo(() => {
    return {
      withdrawFeeAmount: new BigNumber(withdrawFeeAmount || 0),
      withdrawLockAmount,
      depositRolloverList,
    }
  }, [withdrawFeeAmount, withdrawLockAmount, depositRolloverList, selectedToken])
}

const useWithdrawCrypto = ({ selectedToken, bonuses }: IUseWithdrawCryptoControlsProps) => {
  const dispatch = useAppDispatch()
  const minWithdrawAmountDefault = useAppSelector((state) => state.app.minWithdrawTokenAmounts)
  const { wallet } = useAppSelector((state) => state.auth)
  const { withdrawFeeAmount, withdrawLockAmount, depositRolloverList } = useWithdrawRestrictAmounts(selectedToken)

  const bonusWithdrawAmount = useMemo(() => {
    if (selectedToken === HUSD_TOKEN) return BIG_ZERO
    return bonuses
      ?.filter((bonus) => checkTokenEqual(bonus.bonusAmount.token, selectedToken))
      ?.reduce((result, value) => result.plus(value.bonusAmount.amount), new BigNumber(0))
  }, [bonuses, selectedToken])

  const selectTokenAmount = useTokenAppBalance(selectedToken)
  const { username } = useUserInfo()
  const method = useTokenPaymentMethod(selectedToken)
  const { isAuthByEmail, isAuthByTelegram, isAuthByWallet } = useAuthBy()

  const { addressNotAllowed, isFetching } = useWithdrawAddressNotAllowed(selectedToken)

  const [presentDepositRolloverModal] = useDepositRolloverModal()

  const handleOpenDepositRolloverModal = () => {
    presentDepositRolloverModal({ depositRolloverList })
  }

  const totalDepositRollover = useMemo(() => {
    return depositRolloverList.reduce((total, item) => total.plus(item.lockAmount), BIG_ZERO)
  }, [depositRolloverList])

  const minWithdrawAmount = useMemo(
    () => minWithdrawAmountDefault?.[selectedToken?.code] || '',
    [minWithdrawAmountDefault, selectedToken?.code],
  )

  const withdrawalAmount = useMemo(() => {
    const availableAmount = selectTokenAmount?.amount?.minus(withdrawLockAmount.lockAmount).minus(totalDepositRollover)
    return availableAmount?.gt(0) ? availableAmount : BIG_ZERO
  }, [selectTokenAmount, withdrawLockAmount, totalDepositRollover])

  const totalMinWithdrawAmount = new BigNumber(withdrawFeeAmount).plus(minWithdrawAmount)

  const isValidWithdrawToOwnerAddress = useMemo(() => {
    if (method === PaymentMethod.TransferToken) return false
    if (wallet && wallet.address) {
      const networkType = getNetworkType(selectedToken.network)
      return validateUserAddressByNetwokType(wallet.address, networkType)
    }
  }, [selectedToken, wallet?.address])

  const form = useForm({
    amount: {
      validators: [FormValidator.lt(totalMinWithdrawAmount), FormValidator.max(withdrawalAmount)],
      value: '',
    },
    memo: {
      validators: [],
      value: '',
    },
    receiveAddress: {
      validators: isValidWithdrawToOwnerAddress
        ? [FormValidator.required]
        : [
            FormValidator.required,
            FormValidator.address(selectedToken.network),
            FormValidator.notEqual(addressNotAllowed),
          ],
      value: !isValidWithdrawToOwnerAddress ? '' : wallet?.address || username,
    },
  })

  const [allowComment, typeComment] = useMemo(() => {
    if (isTonChain(selectedToken.network)) {
      return [true, 'string']
    }
    if (isXrpChain(selectedToken.network)) {
      return [true, 'number']
    }

    return [false, '']
  }, [selectedToken])

  const onWithdrawSuccess = async (txnCode: string) => {
    dispatch(addPendingWithdrawTransaction({ code: txnCode }))
    form.controls.amount.onValueChanged('')
  }

  useEffect(() => {
    form.controls.amount.clear()
    form.controls.memo.clear()
    form.controls.receiveAddress.onValueChanged(!isValidWithdrawToOwnerAddress ? '' : wallet?.address || username)
  }, [selectedToken])

  return useMemo(() => {
    const { lockAmount, bonusId } = withdrawLockAmount
    const isShowDepositRollover = totalDepositRollover.gt(0)
    const isShowLockAmount = lockAmount.gt(0)

    const valueDisplay = new BigNumber(form.states.amount.value || 0)
      .minus(withdrawFeeAmount)
      .decimalPlaces(selectedToken.decimals, BigNumber.ROUND_FLOOR)
    const inputDisplay = new BigNumber(form.states.amount.value || 0).decimalPlaces(
      selectedToken.decimals,
      BigNumber.ROUND_FLOOR,
    )

    return {
      handler: {
        openDepositRolloverModal: handleOpenDepositRolloverModal,
        onWithdrawSuccess,
      },
      data: {
        valueFormatDecimal: valueDisplay,
        inputFormatDecimal: inputDisplay,
        withdrawalAmount,
        form,
        withdrawFee: withdrawFeeAmount,

        minWithdrawAmount,
        totalMinWithdrawAmount,
        lockAmount,
        bonusId,
        depositRolloverList,
        addressNotAllowed,
        totalDepositRollover,
        isValidWithdrawToOwnerAddress,
        allowComment,
        typeComment,
        bonusWithdrawAmount,
      },
      status: {
        isFetching,
        isTraditionalWithdraw: isAuthByEmail || isAuthByTelegram,
        isWalletWithraw: isAuthByWallet,
        isShowDepositRollover,
        isShowLockAmount,
      },
    }
  }, [
    withdrawFeeAmount,
    withdrawLockAmount,
    minWithdrawAmount,
    depositRolloverList,
    addressNotAllowed,
    isFetching,
    totalDepositRollover,
    handleOpenDepositRolloverModal,
    onWithdrawSuccess,
    isValidWithdrawToOwnerAddress,
    allowComment,
    typeComment,
    bonusWithdrawAmount,
    isAuthByEmail,
    isAuthByTelegram,
    isAuthByWallet,
  ])
}

const useWithdrawFiatMethods = ({
  selectedCurrency,
  onCallbackResponse,
}: {
  selectedCurrency: Token
  onCallbackResponse?: (response: [string, WithdrawFiatPaymentMethod[]][]) => void
}) => {
  const [methods, setMethods] = useState<[string, WithdrawFiatPaymentMethod[]][]>(null)
  const [isFetching, setIsFetcing] = useState(false)
  const { execute } = useRequest()

  useEffect(() => {
    const fetch = async () => {
      try {
        setIsFetcing(true)
        const response = await execute(
          PaymentService.getWithdrawFiatBankMethodList({ currency: selectedCurrency.code }),
        )

        if (response?.data) {
          setMethods(response.data)
        }
        if (onCallbackResponse) {
          onCallbackResponse(response?.data)
        }
      } finally {
        setIsFetcing(false)
      }
    }

    if (selectedCurrency) {
      fetch()
    }
  }, [selectedCurrency])

  return {
    methods,
    isFetching,
  }
}

export const useWithdrawFiat = ({ selectedToken: selectedCurrency }: IUseWithdrawFiatControlsProps) => {
  const { execute } = useRequest()
  const { t } = useTranslation()
  const [onPresentWithdrawInProgressModal] = useModal(WithdrawInProgressModal)

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<WithdrawFiatPaymentMethod>(null)
  const [withdrawFee, setWithdrawFee] = useState('')
  const currencyInfo = useTokenInfo(selectedCurrency)
  const currencyAppBalanceAmount = useTokenAppBalance(selectedCurrency)
  const { lockAmount, depositRolloverList } = useWithdrawInfo(selectedCurrency)
  const { totalDepositRollover, openDepositRollovermodal } = useDepositRolloverOverview(depositRolloverList)
  const refForm = useRef<HTMLDivElement>(null)

  const { isFetching, methods } = useWithdrawFiatMethods({
    selectedCurrency,
    onCallbackResponse: () => {
      setSelectedPaymentMethod(null)
    },
  })

  const handleWithdrawAmountChanged = (value) => {
    const fee = new BigNumber(value || 0)
      .dividedBy(100)
      .multipliedBy(selectedPaymentMethod?.feePercent)
      .plus(selectedPaymentMethod?.feeAmount)
      .toFixed(0, BigNumber.ROUND_UP)
    const totalAmount = new BigNumber(value || 0).minus(fee).toFixed(0)
    setWithdrawFee(fee.toString())
    setTotalWithdrawAmount(totalAmount.toString())
    returnedForm.controls.amount.onValueChanged(value)
  }

  const handleConfirmWithdraw = async (withdrawTransaction: FiatPaymentTransaction) => {
    const response = await execute(PaymentService.getWithdrawal(withdrawTransaction.code))
    onPresentWithdrawInProgressModal({
      paymentFiatTransaction: {
        ...withdrawTransaction,
        method: selectedPaymentMethod.type,
        name: selectedPaymentMethod.name,
      },
      transactionDetails: response?.data,
    })
  }

  const tokenBalanceAmount: TokenAmount = useMemo(() => {
    const availableAmount = new BigNumber(currencyAppBalanceAmount.amount).minus(lockAmount).minus(totalDepositRollover)
    return {
      amount: availableAmount.gt(0) ? availableAmount : BIG_ZERO,
      token: currencyAppBalanceAmount.token,
    }
  }, [currencyAppBalanceAmount.amount, lockAmount, totalDepositRollover])

  const { isAuthByEmail, isAuthByWallet, isAuthByTelegram } = useAuthBy()
  const returnedForm = useForm({
    amount: {
      value: '',
      validators: [
        FormValidator.min(selectedPaymentMethod?.minAmount),
        FormValidator.max(selectedPaymentMethod?.maxAmount),
        FormValidator.required,
        FormValidator.requiredNumber,
        FormValidator.gt(tokenBalanceAmount.amount?.toString()),
      ],
    },
    bankAccountName: {
      value: '',
      validators: [FormValidator.required, FormValidator.inputLength({ min: 1, max: 50 })],
    },
    bankAccountNumber: {
      value: '',
      validators: [
        FormValidator.required,
        FormValidator.requiredInteger,
        FormValidator.inputLength({ min: 1, max: 50 }),
      ],
    },
    holderMobileNumber: {
      value: '',
      validators: RequiredProvidePhoneNumberBankCodes.includes(selectedPaymentMethod?.code)
        ? [FormValidator.required, FormValidator.requiredInteger, FormValidator.inputLength({ min: 1, max: 30 })]
        : [],
    },
  })

  useEffect(() => {
    returnedForm.controls.amount.clear()
    returnedForm.controls.bankAccountName.clear()
    returnedForm.controls.bankAccountNumber.clear()
    returnedForm.controls.holderMobileNumber.clear()
  }, [selectedCurrency, selectedPaymentMethod])

  const onValueChanged = (key: keyof typeof returnedForm.states, value: string, event: any) => {
    if (key === 'bankAccountName' && !isExceedMaxLength(event, 50)) {
      returnedForm.controls.bankAccountName.onValueChanged(value)
    }
    if (key === 'bankAccountNumber' && !isExceedMaxLength(event, 50)) {
      returnedForm.controls.bankAccountNumber.onValueChanged(value)
    }
    if (key === 'holderMobileNumber' && !isExceedMaxLength(event, 30)) {
      returnedForm.controls.holderMobileNumber.onValueChanged(value)
    }
  }

  const [totalWithdrawAmount, setTotalWithdrawAmount] = useState(returnedForm.states.amount.value)

  const formatErrors = (errors: ValidationError[], optionalFields?: { [key: string]: string }) => {
    if (errors[0] === ValidationError.NotEnough || errors[0] === ValidationError.Insufficient) {
      return t(
        'Total withdraw amount has to be between {{ minAmount }} {{ currency }} and {{ maxAmount }} {{ currency }}',
        {
          minAmount: getFullDisplayBalance(selectedPaymentMethod?.minAmount, 0, 5),
          maxAmount: getFullDisplayBalance(selectedPaymentMethod?.maxAmount, 0, 5),
          currency: selectedPaymentMethod?.currency,
        },
      )
    }

    if (errors[0] === ValidationError.Required) {
      return t(optionalFields?.[errors[0]] || '')
    }
    if (errors[0] === ValidationError.TooLong || errors[0] === ValidationError.TooShort)
      return t('Excess required length')
    if (errors[0] === ValidationError.GreatThan) return t('Insufficient balance')
    if (errors[0] === ValidationError.RequiredInteger) return t(optionalFields?.[errors[0]])
  }

  const isNotAbleToExecuteWithdraw =
    !returnedForm.isValid ||
    !returnedForm.states.bankAccountName.value ||
    !returnedForm.states.amount.value ||
    (!returnedForm.states.holderMobileNumber.value &&
      RequiredProvidePhoneNumberBankCodes.includes(selectedPaymentMethod?.code)) ||
    !returnedForm.states.bankAccountNumber.value ||
    new BigNumber(totalWithdrawAmount).eq(0)

  const isExceedMaxLength = (event, maxLengthNumber) => {
    if (event.target.value.length > maxLengthNumber) {
      event.preventDefault()
      event.stopPropagation()
      return true
    }
    return false
  }

  const isShowMobileNumberField =
    selectedPaymentMethod && RequiredProvidePhoneNumberBankCodes.includes(selectedPaymentMethod?.code)

  const isShowDepositRollover = totalDepositRollover.gt(0)
  const isShowLockAmount = lockAmount.gt(0)

  return {
    handler: {
      onConfirmWithraw: handleConfirmWithdraw,
      onSelectMethod: setSelectedPaymentMethod,
      formatErrors,
      onAmountChange: handleWithdrawAmountChanged,
      onValueChanged,
      openDepositRolloverModal: openDepositRollovermodal,
    },
    status: {
      isFetching,
      isNotAbleToExecuteWithdraw,
      isTraditionalWithdraw: isAuthByEmail || isAuthByTelegram,
      isWalletWithraw: isAuthByWallet,

      isShowMobileNumberField,
      isShowLockAmount,
      isShowDepositRollover,
    },
    data: {
      selectedPaymentMethod,
      mobileRefForm: refForm,
      returnedForm,
      methods,
      withdrawFee,
      currencyInfo,
      totalDepositRollover,
      tokenBalanceAmount,
      lockAmount,
      totalWithdrawAmount,
      buttonPayload: {
        selectedCurrency,
        fee: withdrawFee,
        value: returnedForm.states.amount.value,
        bankAccountName: returnedForm.states.bankAccountName?.value,
        bankAccountNumber: returnedForm.states.bankAccountNumber?.value?.toString(),
        accountMobileNumber: returnedForm.states.holderMobileNumber?.value,
        bankCode: selectedPaymentMethod?.code,
      },
    },
  }
}

export const useWithdrawControls = <T>(props: UseWithdrawControlProps) => {
  switch (props.mode) {
    case WithdrawModeEnums.CRYPTO:
      const controlsCrypto = useWithdrawCrypto({ ...props })
      return {
        mode: WithdrawModeEnums.CRYPTO,
        controls: controlsCrypto as T,
      }
    case WithdrawModeEnums.FIAT:
      return {
        mode: WithdrawModeEnums.FIAT,
        controls: useWithdrawFiat({ ...props }) as T,
      }
    default:
      throw new Error('Invalid withdraw mode')
  }
}
